import React, { useCallback, useMemo, useEffect } from "react";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { Hidden } from "@material-ui/core";
import {
  HvInput,
  HvTextArea,
  HvGrid,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";

import {
  useIncidentTypes,
  usePriorities,
  useSeverity,
  useStatus,
} from "lib/api";

import {
  IncidentTypeDropdown,
  PriorityDropdown,
  SeverityDropdown,
  StatusDropdown,
} from "components/form";

import useStyles from "./styles";

interface DetailsProps {
  mode?: "add" | "edit" | "selection";
}

const Details: React.FC<DetailsProps> = ({ mode = "add" }) => {
  const { t } = useTranslation("incidentAddEdit");
  const classes = useStyles();

  const { data: typeData } = useIncidentTypes(undefined, {
    revalidateOnFocus: false,
  });
  const { data: statusData } = useStatus({
    revalidateOnFocus: false,
  });
  const { data: priorityData } = usePriorities({
    revalidateOnFocus: false,
  });
  const { data: severityData } = useSeverity({
    revalidateOnFocus: false,
  });

  const availableStatuses = useMemo(() => {
    return mode === "add"
      ? statusData?.data.filter((status) => status.inc_status_type !== "Closed")
      : statusData?.data;
  }, [mode, statusData]);

  const {
    values,
    setFieldTouched,
    setFieldValue,
    status,
    setStatus,
    setValues,
    errors,
    touched,
  } = useFormikContext<IncidentDetailsFormData>();

  const { serverSideErrors = {} } = status;

  const parseStatus = (field: keyof IncidentDetailsFormData) =>
    serverSideErrors[field] || (errors[field] && touched[field])
      ? "invalid"
      : "valid";

  const parseStatusMessage = (field: keyof IncidentDetailsFormData) => {
    if (serverSideErrors[field]) {
      return serverSideErrors[field];
    }

    return errors[field] && touched[field] ? errors[field] : "";
  };

  const setValueOnForm = useCallback(
    (property: string, selection: unknown) => {
      setFieldValue(property, selection);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldValue, setStatus]
  );

  useEffect(() => {
    if (mode === "edit") return;
    if (statusData?.data && statusData?.data.length > 0) {
      const statusDataDefault = statusData?.data.find(
        (s) => s.status_is_default === true
      );
      setValueOnForm("status", statusDataDefault?.pk_status_id);
    }
    if (priorityData?.data && priorityData?.data.length > 0) {
      const priorityDataDefault = priorityData?.data.find(
        (p) => p.prt_is_default === true
      );
      setValueOnForm("priority", priorityDataDefault?.pk_prt_id);
    }
    if (severityData?.data && severityData?.data.length > 0) {
      const severityDataDefault = severityData?.data.find(
        (s) => s.svrt_is_default === true
      );
      setValueOnForm("severity", severityDataDefault?.pk_svrt_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData, priorityData, severityData]);

  return (
    <HvGrid
      className={clsx(classes.root, classes.fields)}
      container
      item
      xs={12}
      lg={9}
    >
      {mode !== "selection" && (
        <>
          <HvGrid xs={12} md={6} item>
            <HvInput
              className={classes.input}
              name="name"
              label={t("details.fields.name")}
              placeholder={t("details.fields.namePlaceholder")}
              inputProps={{
                autoComplete: "off",
              }}
              required
              value={values.name}
              status={parseStatus("name")}
              statusMessage={parseStatusMessage("name")}
              onBlur={() => setFieldTouched("name", true, false)}
              onChange={(evt, value) => {
                setValueOnForm("name", value);
              }}
            />
          </HvGrid>
          <Hidden smDown>
            <HvGrid item xs={6} md={6} role="presentation" />
          </Hidden>
        </>
      )}
      <HvGrid item xs={12} md={6}>
        <IncidentTypeDropdown
          className={classes.input}
          name="type"
          types={typeData?.data}
          status={parseStatus("type")}
          statusMessage={parseStatusMessage("type")}
          label={t("details.fields.type")}
          placeholder={t("details.fields.typePlaceholder")}
          required
          disablePortal={mode === "selection"}
          defaultValue={values.type}
          disabled={mode === "edit"}
          onToggle={() => setFieldTouched("type", true, false)}
          onChange={(selection) => {
            const selectedValue = selection as ListValueProp;
            if (!selectedValue) {
              setValueOnForm("type", undefined);
              return;
            }
            const id = Number(selectedValue?.id);
            setValueOnForm("type", id);
            const typeAttributes = typeData?.data.find(
              (type) => type.pk_inc_type_id === id
            )?.attributes;
            const attrKeys = Object.keys(typeAttributes || {});
            const attrValues = Object.values(typeAttributes || {});
            if (attrKeys) {
              // We need to set the type id again here, otherwise it will be overridden
              // with `undefined` and the validation will fail.
              setValues((existing) => {
                return {
                  ...existing,
                  type: id,
                  attributes: attrValues.map((attr, index) => ({
                    id: attrKeys[index],
                    ...attr,
                  })),
                };
              });
            }
          }}
        />
      </HvGrid>
      <HvGrid item xs={12} md={6}>
        <StatusDropdown
          className={classes.input}
          name="status"
          statuses={availableStatuses}
          status={parseStatus("status")}
          statusMessage={parseStatusMessage("status")}
          label={t("details.fields.status")}
          placeholder={t("details.fields.statusPlaceholder")}
          required
          disablePortal={mode === "selection"}
          defaultValue={values.status}
          disabled={mode === "selection"}
          onToggle={() => setFieldTouched("status", true, false)}
          onChange={(selection) => {
            const selectedValue = selection as ListValueProp;
            if (!selectedValue) {
              setValueOnForm("status", undefined);
              return;
            }
            setValueOnForm("status", Number(selectedValue?.id));
          }}
        />
      </HvGrid>
      <HvGrid item xs={12} md={6}>
        <PriorityDropdown
          className={classes.input}
          name="priority"
          priorities={priorityData?.data}
          status={parseStatus("priority")}
          statusMessage={parseStatusMessage("priority")}
          label={t("details.fields.priority")}
          placeholder={t("details.fields.priorityPlaceholder")}
          required
          disablePortal={mode === "selection"}
          defaultValue={values.priority}
          onToggle={() => setFieldTouched("priority", true, false)}
          onChange={(selection) => {
            const selectedValue = selection as ListValueProp;
            if (!selectedValue) {
              setValueOnForm("priority", undefined);
              return;
            }
            setValueOnForm("priority", Number(selectedValue?.id));
          }}
        />
      </HvGrid>
      <HvGrid item xs={12} md={6}>
        <SeverityDropdown
          className={classes.input}
          name="severity"
          severities={severityData?.data}
          status={parseStatus("severity")}
          statusMessage={parseStatusMessage("severity")}
          label={t("details.fields.severity")}
          placeholder={t("details.fields.severityPlaceholder")}
          required
          disablePortal={mode === "selection"}
          defaultValue={values.severity}
          onToggle={() => setFieldTouched("severity", true, false)}
          onChange={(selection) => {
            const selectedValue = selection as ListValueProp;
            if (!selectedValue) {
              setValueOnForm("severity", undefined);
              return;
            }
            setValueOnForm("severity", Number(selectedValue?.id));
          }}
        />
      </HvGrid>
      {mode !== "selection" && (
        <HvGrid item xs={12}>
          <HvTextArea
            className={classes.textarea}
            classes={{
              input: classes.textAreaInput,
            }}
            name="description"
            label={t("details.fields.description")}
            placeholder={t("details.fields.descriptionPlaceholder")}
            rows={5}
            value={values.description}
            status={parseStatus("description")}
            statusMessage={parseStatusMessage("description")}
            onBlur={() => setFieldTouched("description", true, false)}
            onChange={(evt, value) => {
              setValueOnForm("description", value);
            }}
          />
        </HvGrid>
      )}
    </HvGrid>
  );
};

export default Details;
